import React from 'react';
import {
  RocketOutlined,
  SafetyOutlined,
  DollarOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  BankOutlined,
  ArrowRightOutlined,
  TeamOutlined,
  TransactionOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { SEOHead } from '../components/SEO/SEOHead';
import { StructuredData } from '../components/SEO/StructuredData';
import ScrollSmootherComponent from '../components/ScrollSmoother';
import PublicHeader from '../components/landing/PublicHeader';
import PublicFooter from '../components/landing/PublicFooter';
import PageHero from '../components/landing/PageHero';
import CtaSection from '../components/landing/CtaSection';
import Reveal from '../components/landing/Reveal';
import CountUp from '../components/landing/CountUp';
import { useTilt } from '../components/landing/useTilt';
import '../components/landing/landing.css';
import './Why.css';

type ReasonCardProps = {
  index: number;
  icon: React.ReactNode;
  title: string;
  description: string;
};

const ReasonCard: React.FC<ReasonCardProps> = ({ index, icon, title, description }) => {
  const { ref, onMouseMove, onMouseLeave } = useTilt<HTMLDivElement>(6);
  return (
    <div ref={ref} className="ts-reason ts-tilt" onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
      <div className="ts-tilt-shine" />
      <div className="ts-reason-top">
        <span className="ts-reason-icon">{icon}</span>
        <span className="ts-reason-index">0{index + 1}</span>
      </div>
      <h2 className="ts-reason-title">{title}</h2>
      <p className="ts-reason-desc">{description}</p>
    </div>
  );
};

const Why: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const reasons = [
    { icon: <ThunderboltOutlined />, title: t.whyPage.r1Title, description: t.whyPage.r1Desc },
    { icon: <DollarOutlined />, title: t.whyPage.r2Title, description: t.whyPage.r2Desc },
    { icon: <SafetyOutlined />, title: t.whyPage.r3Title, description: t.whyPage.r3Desc },
    { icon: <GlobalOutlined />, title: t.whyPage.r4Title, description: t.whyPage.r4Desc },
    { icon: <RocketOutlined />, title: t.whyPage.r5Title, description: t.whyPage.r5Desc },
    { icon: <BankOutlined />, title: t.whyPage.r6Title, description: t.whyPage.r6Desc },
  ];

  const stats = [
    { title: t.stats.activeAgents, value: '1000+', icon: <TeamOutlined /> },
    { title: t.stats.transactions, value: '50K+', icon: <TransactionOutlined /> },
    { title: t.stats.uptime, value: '99.9%', icon: <ClockCircleOutlined /> },
    { title: t.stats.support, value: '24/7', icon: <CustomerServiceOutlined /> },
  ];

  return (
    <>
      <SEOHead
        title={t.whyPage.seoTitle}
        description={t.whyPage.seoDescription}
        canonical="/why/"
        keywords="why telegram stars payment, telegram stars payment benefits, telegram stars payment advantages, telegram stars payment platform features"
      />
      <StructuredData type="page" title={t.whyPage.seoTitle} description={t.whyPage.seoDescription} />

      <PublicHeader />

      <ScrollSmootherComponent smooth={1.2} effects normalizeScroll>
        <div className="ts-landing ts-why-page">
          <PageHero eyebrow={t.nav.why} title={t.whyPage.title} subtitle={t.whyPage.subtitle}>
            <button type="button" className="ts-btn ts-btn--primary" onClick={() => navigate('/login')}>
              {t.hero.cta}
              <ArrowRightOutlined />
            </button>
          </PageHero>

          {/* Chiffres clés en bandeau */}
          <section className="ts-why-strip">
            <Reveal stagger={0.1} className="ts-container ts-why-strip-grid" y={30}>
              {stats.map((s, i) => (
                <div key={i} className="ts-why-strip-item">
                  <span className="ts-why-strip-icon">{s.icon}</span>
                  <div>
                    <div className="ts-why-strip-value">
                      <CountUp value={s.value} />
                    </div>
                    <div className="ts-why-strip-label">{s.title}</div>
                  </div>
                </div>
              ))}
            </Reveal>
          </section>

          {/* Raisons */}
          <section className="ts-section ts-reasons">
            <div className="ts-container">
              <Reveal className="ts-section-header">
                <span className="ts-eyebrow ts-eyebrow--light">
                  <span className="ts-eyebrow-dot" />
                  {t.nav.why}
                </span>
                <h2 className="ts-title ts-title--light">{t.features.title}</h2>
                <p className="ts-lead ts-lead--light">{t.features.subtitle}</p>
              </Reveal>
              <Reveal stagger={0.08} className="ts-reasons-grid" y={60}>
                {reasons.map((r, i) => (
                  <ReasonCard key={i} index={i} icon={r.icon} title={r.title} description={r.description} />
                ))}
              </Reveal>
            </div>
          </section>

          <CtaSection />
          <PublicFooter />
        </div>
      </ScrollSmootherComponent>
    </>
  );
};

export default Why;
